<script setup lang="ts">
  import {
    Dropdown,
    DropdownPlacement,
    FontIcon,
    Tooltip,
  } from '@tundr/ds-components';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { computed, inject, onUnmounted, ref, useId, watch } from 'vue';
  import { type RouteLocationRaw, useRouter } from 'vue-router';
  import { SIDEBAR_COLLAPSED_KEY } from '../../constants';
  import { useSidebarStore } from '../../store/sidebar.store';
  import { SidebarSubItem } from '../SidebarSubItem';
  import {
    leafVariantClasses,
    sidebarItemChevronClass,
    sidebarItemChevronExpandedClass,
    sidebarItemClass,
    sidebarItemCollapsedAnchorClass,
    sidebarItemCollapsedTriggerClass,
    sidebarItemIconClass,
    sidebarItemIconVariants,
    sidebarItemSelectedClass,
    sidebarItemVariants,
    sidebarLeafClass,
    sidebarLeafIconClass,
    sidebarLeafLabelClass,
    sidebarLeafSelectedClass,
    sidebarSectionLabelClass,
    subItemMenuClass,
    subItemMenuContentClass,
    subItemMenuTitleClass,
    subItemsInnerClass,
    subItemsWrappeExpandedClasses,
    subItemsWrapperClass,
  } from './SidebarItem.css';
  import type { SidebarItemProps } from './SidebarItem.types';

  const props = defineProps<SidebarItemProps>();

  const router = useRouter();
  const sidebarStore = useSidebarStore();
  const collapsed = inject(
    SIDEBAR_COLLAPSED_KEY,
    computed(() => sidebarStore.collapsed),
  );

  const subItemsId = useId();

  const matchesRoute = (to?: RouteLocationRaw) => {
    if (!to) return false;
    const resolved = router.resolve(to);
    const currentPath = router.currentRoute.value.path;
    return (
      currentPath === resolved.path ||
      currentPath.startsWith(`${resolved.path}/`)
    );
  };

  const hasVisibleSubItems = computed(() =>
    props.subItems?.some((sub) => !sub.isHidden),
  );

  const isLeaf = computed(
    () => !hasVisibleSubItems.value && Boolean(props.to || props.onClick),
  );

  const subItemIsSelected = computed(() =>
    props.subItems?.some((subItem) => matchesRoute(subItem.to)),
  );

  const leafIsActive = computed(() => matchesRoute(props.to));

  const isActive = computed(() =>
    isLeaf.value ? leafIsActive.value : subItemIsSelected.value,
  );

  const expanded = ref(false);

  watch(
    subItemIsSelected,
    (selected) => {
      if (selected) {
        expanded.value = true;
      }
    },
    { immediate: true },
  );

  const handleToggle = () => {
    expanded.value = !expanded.value;
  };

  const tundrIconValues = new Set<string>(Object.values(TUNDR_ICONS));

  const displayIcon = computed<TUNDR_ICONS>(() => {
    if (!isActive.value) return props.icon;
    const solidIcon = `${props.icon}-solid`;
    return tundrIconValues.has(solidIcon)
      ? (solidIcon as TUNDR_ICONS)
      : props.icon;
  });

  const resolvedHref = computed(() =>
    props.to ? router.resolve(props.to).href : undefined,
  );

  const onLeafNavigate = () => {
    if (props.to) {
      router.push(props.to);
    }
    if (props.onClick) {
      props.onClick();
    }
  };

  const onLeafMiddleClick = () => {
    props.onMiddleClick?.();
  };

  const leafClasses = computed(() => [
    sidebarLeafClass,
    leafVariantClasses[sidebarStore.variant],
    { [sidebarLeafSelectedClass]: leafIsActive.value },
  ]);

  const subItemsWrapperClasses = computed(() => {
    return [
      subItemsWrapperClass,
      { [subItemsWrappeExpandedClasses]: expanded.value },
    ];
  });

  const sidebarItemClasses = computed(() => [
    sidebarItemClass,
    sidebarItemVariants[sidebarStore.variant],
    { [sidebarItemSelectedClass]: subItemIsSelected.value },
  ]);

  const iconClasses = computed(() => [
    sidebarItemIconClass,
    sidebarItemIconVariants[sidebarStore.variant],
  ]);

  const HOVER_CLOSE_DELAY_MS = 150;

  const menuOpen = ref(false);
  let menuCloseTimer: ReturnType<typeof setTimeout> | null = null;

  const cancelScheduledMenuClose = () => {
    if (!menuCloseTimer) return;
    clearTimeout(menuCloseTimer);
    menuCloseTimer = null;
  };

  const isMousePointer = (event: PointerEvent) =>
    !event.pointerType || event.pointerType === 'mouse';

  const openMenuOnHover = (event: PointerEvent) => {
    if (!isMousePointer(event)) return;
    cancelScheduledMenuClose();
    menuOpen.value = true;
  };

  const scheduleMenuClose = (event: PointerEvent) => {
    if (!isMousePointer(event)) return;
    cancelScheduledMenuClose();
    menuCloseTimer = setTimeout(() => {
      menuOpen.value = false;
      menuCloseTimer = null;
    }, HOVER_CLOSE_DELAY_MS);
  };

  const closeMenu = () => {
    cancelScheduledMenuClose();
    menuOpen.value = false;
  };

  const onMenuOpenChange = (open: boolean) => {
    cancelScheduledMenuClose();
    menuOpen.value = open;
  };

  onUnmounted(cancelScheduledMenuClose);

  const subMenuClasses = computed(() => {
    return [
      subItemMenuClass,
      { [sidebarItemSelectedClass]: subItemIsSelected.value },
    ];
  });
</script>

<template>
  <template v-if="!isHidden && (hasVisibleSubItems || isLeaf)">
    <template v-if="isLeaf">
      <a
        v-if="to"
        :href="resolvedHref"
        :data-testid="testId"
        :class="leafClasses"
        :aria-current="leafIsActive ? 'page' : undefined"
        @click.stop.prevent="onLeafNavigate"
        @auxclick="onLeafMiddleClick"
      >
        <FontIcon :icon-name="displayIcon" :class="sidebarLeafIconClass" />
        <span :class="sidebarLeafLabelClass">{{ label }}</span>
      </a>
      <div
        v-else
        :data-testid="testId"
        :class="leafClasses"
        role="link"
        tabindex="0"
        :aria-current="leafIsActive ? 'page' : undefined"
        @click.stop.prevent="onLeafNavigate"
        @keydown.enter.prevent="onLeafNavigate"
      >
        <FontIcon :icon-name="displayIcon" :class="sidebarLeafIconClass" />
        <span :class="sidebarLeafLabelClass">{{ label }}</span>
      </div>
    </template>

    <template v-else>
      <div v-if="!collapsed">
        <div
          :class="sidebarItemClasses"
          role="button"
          tabindex="0"
          :aria-expanded="expanded"
          :aria-controls="subItemsId"
          @click="handleToggle"
          @keydown.enter.prevent="handleToggle"
          @keydown.space.prevent="handleToggle"
        >
          <FontIcon :icon-name="displayIcon" :class="iconClasses" />
          <div :class="sidebarSectionLabelClass">{{ label }}</div>
          <FontIcon
            :icon-name="TUNDR_ICONS.CHEVRON_DOWN"
            :class="[
              sidebarItemChevronClass,
              { [sidebarItemChevronExpandedClass]: expanded },
            ]"
          />
        </div>
        <div :id="subItemsId" :class="subItemsWrapperClasses">
          <div :class="subItemsInnerClass">
            <template v-for="subItem in subItems" :key="subItem.label">
              <SidebarSubItem v-if="!subItem.isHidden" v-bind="subItem" />
            </template>
          </div>
        </div>
      </div>

      <div
        v-else
        :class="subMenuClasses"
        @pointerenter="openMenuOnHover"
        @pointerleave="scheduleMenuClose"
      >
        <Dropdown
          :open="menuOpen"
          :placement="DropdownPlacement.BOTTOM_START"
          :auto-focus="false"
          trigger-as-child
          @update:open="onMenuOpenChange"
        >
          <template #trigger>
            <span :class="sidebarItemCollapsedAnchorClass">
              <Tooltip :title="label">
                <button
                  type="button"
                  :class="sidebarItemCollapsedTriggerClass"
                  :aria-label="label"
                >
                  <FontIcon
                    test-id="sidebar-item-collapsed-menu-icon"
                    :class="iconClasses"
                    :icon-name="displayIcon"
                  />
                </button>
              </Tooltip>
            </span>
          </template>
          <!-- biome-ignore lint/a11y/noStaticElementInteractions: popover container, not a control; the interactive elements are the sub-items inside it -->
          <!-- biome-ignore lint/a11y/useKeyWithClickEvents: the capture handler only closes the menu after a sub-item is activated, which keyboard activation also dispatches as a click -->
          <div
            :class="subItemMenuContentClass"
            @pointerenter="cancelScheduledMenuClose"
            @pointerleave="scheduleMenuClose"
            @click.capture="closeMenu"
          >
            <div :class="subItemMenuTitleClass">{{ label }}</div>
            <template v-for="subItem in subItems" :key="subItem.label">
              <SidebarSubItem v-if="!subItem.isHidden" v-bind="subItem" />
            </template>
          </div>
        </Dropdown>
      </div>
    </template>
  </template>
</template>
